/**
 * The score's instruments, synthesized with Web Audio: each one a small model of how the real thing makes sound, not a
 * sample. Bowed strings (a sawtooth "bow" through the body's resonances, with vibrato that arrives late and a breath of
 * bow noise), flute and clarinet (a few partials and breath), a horn (a swell that opens its filter), plucked strings
 * (guitar, bass, harp, pizzicato: Karplus-Strong, rendered once per pitch), the whole family of bells (tubular,
 * handbell, celesta, glockenspiel, music box), marimba and vibraphone, a felt piano (slightly inharmonic partials
 * and a soft hammer), a sung "aah", and the soft FM electric piano. Every voice plays into the shared room (Bus).
 *
 * Recorded instruments, behind a switch: with a Sampler installed (useSamples), a voice whose sample set has loaded
 * plays the recording instead (strings by section or solo, pizzicato, tremolo, flute, clarinet, horn, harp, guitar,
 * electric bass, upright piano, tubular bells, glockenspiel, vibraphone, marimba). Anything not loaded, and the voices
 * nobody recorded for free (choir, music box, celesta, handbell, electric piano, pads, the sub), stay synthesized.
 */
import type { Sampler, SetKey } from "./sampler";

export type Ctx = BaseAudioContext;
export const hz = (midi: number) => 440 * 2 ** ((midi - 69) / 12);
const clamp = (x: number, a: number, b: number) => Math.max(a, Math.min(b, x));

// ---- the room ------------------------------------------------------------------------------------------------------

export interface Bus { dry: GainNode; verb: GainNode; echo: GainNode; delay: DelayNode; air: GainNode; out: GainNode; lfo: OscillatorNode }

/** A long, dark, decorrelated stereo tail (the score's hall). */
function hallImpulse(ctx: Ctx, seconds = 5.5): AudioBuffer {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    let lp = 0;
    for (let i = 0; i < len; i++) {
      const t = i / len;
      const a = 0.55 - 0.47 * t; // darker as it decays
      lp += a * ((Math.random() * 2 - 1) - lp);
      const pre = i < ctx.sampleRate * 0.012 ? i / (ctx.sampleRate * 0.012) : 1;
      d[i] = lp * pre * Math.pow(1 - t, 2.2) * Math.exp(-t * 2.4);
    }
  }
  return buf;
}

const noiseCache = new WeakMap<Ctx, AudioBuffer>();
/** Two seconds of white noise, made once per context (bow, breath, hammer). */
function noise(ctx: Ctx): AudioBuffer {
  let b = noiseCache.get(ctx);
  if (!b) {
    b = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const d = b.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    noiseCache.set(ctx, b);
  }
  return b;
}

export function buildBus(ctx: Ctx, dest: AudioNode): Bus {
  const out = ctx.createGain(); out.gain.value = 0;
  const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 36;
  const glue = ctx.createDynamicsCompressor();
  glue.threshold.value = -22; glue.ratio.value = 2.5; glue.attack.value = 0.04; glue.release.value = 0.4; glue.knee.value = 12;
  const dry = ctx.createGain();
  const verb = ctx.createGain();
  const hall = ctx.createConvolver(); hall.buffer = hallImpulse(ctx);
  const echo = ctx.createGain();
  const delay = ctx.createDelay(2); delay.delayTime.value = 0.6;
  const fb = ctx.createGain(); fb.gain.value = 0.3;
  const dark = ctx.createBiquadFilter(); dark.type = "lowpass"; dark.frequency.value = 2400;
  echo.connect(delay); delay.connect(dark); dark.connect(fb); fb.connect(delay); dark.connect(verb); dark.connect(dry);
  verb.connect(hall); hall.connect(hp); dry.connect(hp);
  hp.connect(glue); glue.connect(out); out.connect(dest);
  const src = ctx.createBufferSource(); src.buffer = noise(ctx); src.loop = true;
  const band = ctx.createBiquadFilter(); band.type = "bandpass"; band.frequency.value = 900; band.Q.value = 0.6;
  const soften = ctx.createBiquadFilter(); soften.type = "lowpass"; soften.frequency.value = 1400;
  const air = ctx.createGain(); air.gain.value = 0;
  src.connect(band); band.connect(soften); soften.connect(air); air.connect(verb); air.connect(dry);
  src.start();
  const lfo = ctx.createOscillator(); lfo.frequency.value = 0.06; lfo.start();
  return { dry, verb, echo, delay, air, out, lfo };
}

/**
 * Sends one voice to the room. Voices share a few fixed lanes (a pan position and a send mix), so a note adds two or
 * three nodes, not eight: what keeps a busy passage light enough for a phone.
 */
const lanes = new WeakMap<Bus, Map<string, AudioNode>>();
export function send(ctx: Ctx, bus: Bus, node: AudioNode, pan: number, dry: number, verb: number, echo = 0) {
  let m = lanes.get(bus);
  if (!m) lanes.set(bus, (m = new Map()));
  const p = Math.round(clamp(pan, -1, 1) * 3) / 3;
  const key = `${p}|${dry}|${verb}|${echo}`;
  let lane = m.get(key);
  if (!lane) {
    const panner = ctx.createStereoPanner(); panner.pan.value = p;
    const g = (v: number, to: AudioNode) => { if (v <= 0) return; const x = ctx.createGain(); x.gain.value = v; panner.connect(x); x.connect(to); };
    g(dry, bus.dry); g(verb, bus.verb); g(echo, bus.echo);
    m.set(key, (lane = panner));
  }
  node.connect(lane);
}

const waveCache = new WeakMap<Ctx, Record<string, PeriodicWave>>();
/** Soft harmonics (1/n^1.7), or odd ones only (the clarinet's hollow tone). */
function wave(ctx: Ctx, kind: "soft" | "odd"): PeriodicWave {
  const c = waveCache.get(ctx) ?? {};
  waveCache.set(ctx, c);
  if (!c[kind]) {
    const n = 24, real = new Float32Array(n), imag = new Float32Array(n);
    for (let k = 1; k < n; k++) imag[k] = kind === "odd" ? (k % 2 ? 1 / Math.pow(k, 1.1) : 0.02 / k) : 1 / Math.pow(k, 1.7) * (k % 2 ? 1 : 0.6);
    c[kind] = ctx.createPeriodicWave(real, imag);
  }
  return c[kind]!;
}

/** A vibrato that arrives after the note has settled, as players do. */
function vibrato(ctx: Ctx, target: AudioParam, at: number, f: number, rate: number, depth: number, delay: number, end: number) {
  const lfo = ctx.createOscillator(); lfo.frequency.value = rate * (0.94 + Math.random() * 0.12);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0, at); g.gain.setValueAtTime(0, at + delay); g.gain.linearRampToValueAtTime(f * depth, at + delay + 0.5);
  lfo.connect(g); g.connect(target);
  lfo.start(at); lfo.stop(end);
}

export interface Held { stop(at: number): void }

// ---- recorded instruments (optional) ---------------------------------------------------------------------------------

let samples: Sampler | null = null;
/** Installs (or, with null, removes) the recorded instruments. Voices fall back to synthesis per set until it loads. */
export function useSamples(s: Sampler | null): void { samples = s; }
/** Starts loading these sets (the score asks when its ensemble changes); a no-op without a Sampler. */
export function prefetch(ctx: Ctx, keys: SetKey[]): Promise<void> { return samples ? samples.preload(ctx, keys) : Promise.resolve(); }

/**
 * Level of each recorded set against the synth voice it replaces (the samples are normalised to an RMS of 0.1; the
 * synth voices are not), measured offline note against note so the arrangements' balance and trims still hold
 * (packages/lab/loom/score: render.py --calibrate).
 */
export const SAMPLE_GAIN: Record<SetKey, number> = {
  "cello.sustain": 9.6, "cello.solo": 10, "cello.tremolo": 4.8, "viola.sustain": 9.9, "viola.tremolo": 5.6, "violin.sustain": 10, "violin.solo": 9.7,
  "strings.pizz": 0.73, "flute.sustain": 6.8, "clarinet.sustain": 9.2, "horn.sustain": 6.2, "harp.pluck": 1.4, "guitar.pluck": 1.17, "bass.pluck": 1.22,
  "piano.soft": 2.8, "tubular.hit": 4.3, "glockenspiel.hit": 2.26, "vibraphone.hit": 2.7, "marimba.hit": 1.26,
};

/** The recorded note, or null (then the caller synthesizes it). */
function recorded(ctx: Ctx, key: SetKey, at: number, midi: number, vel: number, dur: number | null, attack?: number, release?: number): GainNode | null {
  return samples ? samples.note(ctx, key, at, midi, vel * (SAMPLE_GAIN[key] ?? 1), dur, { attack, release }) : null;
}

// ---- bowed strings -------------------------------------------------------------------------------------------------

const BODY = {
  cello: { lp: 2400, f1: 210, f2: 880, bright: 0.45, rate: 5.0, depth: 0.0045 },
  viola: { lp: 3400, f1: 330, f2: 1150, bright: 0.5, rate: 5.3, depth: 0.004 },
  violin: { lp: 6200, f1: 460, f2: 2900, bright: 0.6, rate: 5.8, depth: 0.0038 },
} as const;
export type Bowed = keyof typeof BODY;

/** A bowed note of `dur` seconds: the attack takes a moment, it swells a little, then releases. */
export function bowed(ctx: Ctx, bus: Bus, at: number, midi: number, vel: number, dur: number, kind: Bowed, pan: number, o: { tremolo?: boolean; solo?: boolean } = {}) {
  // Recorded: tremolo (cello and viola sections), a solo player (cello, violin) or the section.
  const key = o.tremolo && kind !== "violin" ? `${kind}.tremolo` : o.solo && kind !== "viola" ? `${kind}.solo` : `${kind}.sustain`;
  const rec = recorded(ctx, key, at, midi, vel, dur, kind === "cello" ? 0.12 : 0.08, 0.18);
  // The solo cello was recorded in an anechoic room: it gets a little more of the hall.
  if (rec) { send(ctx, bus, rec, pan, 0.5, key === "cello.solo" ? 0.75 : 0.6); return; }
  const b = BODY[kind], f = hz(midi);
  const end = at + dur + 0.6;
  const osc = ctx.createOscillator(); osc.type = "sawtooth"; osc.frequency.value = f;
  const osc2 = ctx.createOscillator(); osc2.type = "sawtooth"; osc2.frequency.value = f; osc2.detune.value = 6;
  vibrato(ctx, osc.frequency, at, f, b.rate, b.depth, 0.35, end);
  vibrato(ctx, osc2.frequency, at, f, b.rate, b.depth, 0.4, end);
  const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = b.lp; lp.Q.value = 0.5;
  const body1 = ctx.createBiquadFilter(); body1.type = "peaking"; body1.frequency.value = b.f1; body1.Q.value = 1.3; body1.gain.value = 7;
  const body2 = ctx.createBiquadFilter(); body2.type = "peaking"; body2.frequency.value = b.f2; body2.Q.value = 1.6; body2.gain.value = 5;
  const scoop = ctx.createBiquadFilter(); scoop.type = "peaking"; scoop.frequency.value = 1600; scoop.Q.value = 1; scoop.gain.value = -5;
  const amp = ctx.createGain();
  const attack = kind === "cello" ? 0.22 : 0.14;
  amp.gain.setValueAtTime(0, at);
  amp.gain.linearRampToValueAtTime(vel * 0.8, at + attack);
  amp.gain.linearRampToValueAtTime(vel, at + Math.max(attack + 0.05, dur * 0.55));
  amp.gain.linearRampToValueAtTime(vel * 0.85, at + dur);
  amp.gain.setTargetAtTime(0, at + dur, 0.18);
  let src: AudioNode = amp;
  if (o.tremolo) {
    const trem = ctx.createGain(); trem.gain.value = 0.6;
    const t = ctx.createOscillator(); t.frequency.value = 11 + Math.random() * 2;
    const tg = ctx.createGain(); tg.gain.value = 0.4;
    t.connect(tg); tg.connect(trem.gain); amp.connect(trem); src = trem; t.start(at); t.stop(end);
  }
  osc.connect(lp); osc2.connect(lp); lp.connect(body1); body1.connect(body2); body2.connect(scoop); scoop.connect(amp);
  // A breath of bow noise on the string.
  const bow = ctx.createBufferSource(); bow.buffer = noise(ctx); bow.loop = true;
  const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = Math.min(5000, f * 6); bp.Q.value = 0.9;
  const bg = ctx.createGain(); bg.gain.setValueAtTime(0, at); bg.gain.linearRampToValueAtTime(vel * 0.05, at + 0.05); bg.gain.linearRampToValueAtTime(vel * 0.018, at + 0.3); bg.gain.setTargetAtTime(0, at + dur, 0.1);
  bow.connect(bp); bp.connect(bg); bg.connect(scoop);
  send(ctx, bus, src, pan, 0.5, 0.6);
  osc.start(at); osc2.start(at); bow.start(at, Math.random());
  osc.stop(end); osc2.stop(end); bow.stop(end);
}

// ---- winds -----------------------------------------------------------------------------------------------------------

/** A flute: a near-sine with a little second harmonic, a chiff of breath at the start, a slow vibrato. */
export function flute(ctx: Ctx, bus: Bus, at: number, midi: number, vel: number, dur: number, pan: number) {
  const rec = recorded(ctx, "flute.sustain", at, midi, vel, dur, 0.04, 0.09);
  if (rec) { send(ctx, bus, rec, pan, 0.5, 0.6, 0.12); return; }
  const f = hz(midi), end = at + dur + 0.4;
  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0, at); amp.gain.linearRampToValueAtTime(vel, at + 0.07); amp.gain.linearRampToValueAtTime(vel * 0.88, at + dur); amp.gain.setTargetAtTime(0, at + dur, 0.09);
  const parts: [number, number][] = [[1, 1], [2, 0.18], [3, 0.06]];
  for (const [k, a] of parts) {
    const o = ctx.createOscillator(); o.frequency.value = f * k;
    vibrato(ctx, o.frequency, at, f * k, 4.9, 0.0035, 0.3, end);
    const g = ctx.createGain(); g.gain.value = a; o.connect(g); g.connect(amp); o.start(at); o.stop(end);
  }
  const br = ctx.createBufferSource(); br.buffer = noise(ctx); br.loop = true;
  const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = f * 2; bp.Q.value = 1.5;
  const bg = ctx.createGain(); bg.gain.setValueAtTime(vel * 0.35, at); bg.gain.exponentialRampToValueAtTime(vel * 0.05, at + 0.12); bg.gain.setTargetAtTime(0, at + dur, 0.08);
  br.connect(bp); bp.connect(bg); bg.connect(amp);
  br.start(at, Math.random()); br.stop(end);
  send(ctx, bus, amp, pan, 0.5, 0.6, 0.12);
}

/** A clarinet: odd harmonics (the hollow tone), dark, very little vibrato. */
export function clarinet(ctx: Ctx, bus: Bus, at: number, midi: number, vel: number, dur: number, pan: number) {
  const rec = recorded(ctx, "clarinet.sustain", at, midi, vel, dur, 0.03, 0.07);
  if (rec) { send(ctx, bus, rec, pan, 0.55, 0.5); return; }
  const f = hz(midi), end = at + dur + 0.4;
  const o = ctx.createOscillator(); o.setPeriodicWave(wave(ctx, "odd")); o.frequency.value = f;
  vibrato(ctx, o.frequency, at, f, 4.5, 0.0015, 0.4, end);
  const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 1800 + vel * 2000; lp.Q.value = 0.4;
  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0, at); amp.gain.linearRampToValueAtTime(vel, at + 0.05); amp.gain.linearRampToValueAtTime(vel * 0.9, at + dur); amp.gain.setTargetAtTime(0, at + dur, 0.07);
  o.connect(lp); lp.connect(amp);
  send(ctx, bus, amp, pan, 0.55, 0.5);
  o.start(at); o.stop(end);
}

/** A horn: two warm reeds whose filter opens as the note swells and settles back (brass, played softly). */
export function horn(ctx: Ctx, bus: Bus, at: number, midi: number, vel: number, dur: number, pan: number) {
  const rec = recorded(ctx, "horn.sustain", at, midi, vel, dur, 0.08, 0.2);
  if (rec) { send(ctx, bus, rec, pan, 0.45, 0.75); return; }
  const f = hz(midi), end = at + dur + 0.6;
  const a = ctx.createOscillator(); a.type = "sawtooth"; a.frequency.value = f; a.detune.value = -5;
  const b = ctx.createOscillator(); b.type = "sawtooth"; b.frequency.value = f; b.detune.value = 5;
  const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.Q.value = 0.8;
  lp.frequency.setValueAtTime(f * 1.5, at); lp.frequency.linearRampToValueAtTime(f * (3 + vel * 9), at + Math.min(0.5, dur * 0.4)); lp.frequency.linearRampToValueAtTime(f * 2.5, at + dur);
  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0, at); amp.gain.linearRampToValueAtTime(vel, at + 0.16); amp.gain.linearRampToValueAtTime(vel * 0.8, at + dur); amp.gain.setTargetAtTime(0, at + dur, 0.2);
  a.connect(lp); b.connect(lp); lp.connect(amp);
  send(ctx, bus, amp, pan, 0.45, 0.75);
  a.start(at); b.start(at); a.stop(end); b.stop(end);
}

// ---- plucked strings (Karplus-Strong) --------------------------------------------------------------------------------

const PLUCK = {
  guitar: { dur: 2.6, bright: 0.5, loss: 0.996, body: 180, lp: 3600 },
  bass: { dur: 2.4, bright: 0.22, loss: 0.997, body: 90, lp: 900 },
  harp: { dur: 4, bright: 0.7, loss: 0.9985, body: 400, lp: 7000 },
  pizz: { dur: 0.7, bright: 0.45, loss: 0.982, body: 250, lp: 3000 },
} as const;
export type Plucked = keyof typeof PLUCK;

const ksCache = new WeakMap<Ctx, Map<string, AudioBuffer>>();
/** A plucked string's sound at one pitch, computed once (a noise burst circulating through a lossy delay line). */
function ks(ctx: Ctx, midi: number, kind: Plucked): AudioBuffer {
  let m = ksCache.get(ctx);
  if (!m) ksCache.set(ctx, (m = new Map()));
  const key = `${kind}:${midi}`;
  const hit = m.get(key);
  if (hit) return hit;
  const p = PLUCK[kind], sr = ctx.sampleRate;
  const N = Math.max(2, Math.round(sr / hz(midi) - 0.5));
  const len = Math.floor(sr * p.dur);
  const buf = ctx.createBuffer(1, len, sr);
  const out = buf.getChannelData(0);
  const ring = new Float32Array(N);
  let lp = 0;
  for (let i = 0; i < N; i++) { lp += p.bright * ((Math.random() * 2 - 1) - lp); ring[i] = lp; }
  // Higher notes lose energy faster per second; scale the loss per period so every note rings about as long.
  const loss = Math.pow(p.loss, clamp(N / (sr / 196), 0.25, 2));
  let i = 0;
  for (let n = 0; n < len; n++) {
    const a = ring[i]!, b = ring[(i + 1) % N]!;
    out[n] = a;
    ring[i] = loss * 0.5 * (a + b);
    i = (i + 1) % N;
  }
  const fade = Math.floor(sr * 0.05);
  for (let n = 0; n < fade; n++) out[len - 1 - n]! *= n / fade;
  m.set(key, buf);
  return buf;
}

/** A plucked note: guitar, bass, harp or pizzicato strings. */
export function pluck(ctx: Ctx, bus: Bus, at: number, midi: number, vel: number, kind: Plucked, pan: number) {
  // Recorded: the electric bass was sampled over its lowest octave and a half, so higher notes drop an octave.
  const rec = recorded(ctx, kind === "pizz" ? "strings.pizz" : `${kind}.pluck`, at, kind === "bass" && midi > 46 ? midi - 12 : midi, vel, null);
  if (rec) { send(ctx, bus, rec, pan, kind === "bass" ? 0.9 : 0.6, kind === "harp" ? 0.6 : 0.35, kind === "guitar" ? 0.1 : 0); return; }
  const p = PLUCK[kind];
  const src = ctx.createBufferSource(); src.buffer = ks(ctx, midi, kind);
  const body = ctx.createBiquadFilter(); body.type = "peaking"; body.frequency.value = p.body; body.Q.value = 1.1; body.gain.value = 5;
  const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = p.lp;
  const amp = ctx.createGain(); amp.gain.value = vel;
  src.connect(body); body.connect(lp); lp.connect(amp);
  send(ctx, bus, amp, pan, kind === "bass" ? 0.9 : 0.6, kind === "harp" ? 0.6 : 0.35, kind === "guitar" ? 0.1 : 0);
  src.start(at);
}

// ---- bells, mallets, keys ------------------------------------------------------------------------------------------

/** Additive partials, each with its own decay: bells and bars are sums of slightly out-of-tune sines. */
function partials(ctx: Ctx, bus: Bus, at: number, f: number, vel: number, pan: number, ps: readonly (readonly [number, number, number])[], sendMix: [number, number, number]) {
  const amp = ctx.createGain(); amp.gain.value = vel;
  let longest = 0;
  for (const [ratio, a, decay] of ps) {
    if (f * ratio > 16000) continue;
    const o = ctx.createOscillator(); o.frequency.value = f * ratio;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0, at); g.gain.linearRampToValueAtTime(a, at + 0.003); g.gain.exponentialRampToValueAtTime(0.0001, at + decay);
    o.connect(g); g.connect(amp); o.start(at); o.stop(at + decay + 0.05);
    longest = Math.max(longest, decay);
  }
  send(ctx, bus, amp, pan, ...sendMix);
  return longest;
}

export type BellKind = "tubular" | "handbell" | "celesta" | "glockenspiel" | "musicbox";
/** The bell family across its range: tubular bells low and long, handbells, celesta, glockenspiel, an old music box. */
export function bell(ctx: Ctx, bus: Bus, at: number, midi: number, vel: number, kind: BellKind, pan: number) {
  if (kind === "tubular" || kind === "glockenspiel") {
    const rec = recorded(ctx, `${kind}.hit`, at, midi, vel, null);
    if (rec) { if (kind === "tubular") send(ctx, bus, rec, pan, 0.4, 0.8, 0.1); else send(ctx, bus, rec, pan, 0.5, 0.6, 0.25); return; }
  }
  const f = hz(midi);
  if (kind === "tubular") partials(ctx, bus, at, f, vel, pan, [[0.5, 0.35, 7], [1, 1, 6], [1.19, 0.5, 4.5], [1.56, 0.45, 3.5], [2.0, 0.35, 3], [2.74, 0.2, 2], [3.76, 0.12, 1.4]], [0.4, 0.8, 0.1]);
  else if (kind === "handbell") partials(ctx, bus, at, f, vel, pan, [[1, 1, 3.2], [2.4, 0.4, 1.6], [3.0, 0.25, 1.1], [4.5, 0.12, 0.6]], [0.45, 0.7, 0.2]);
  else if (kind === "celesta") partials(ctx, bus, at, f, vel, pan, [[1, 1, 1.6], [2, 0.25, 0.5], [3.98, 0.12, 0.25]], [0.55, 0.55, 0.25]);
  else if (kind === "glockenspiel") partials(ctx, bus, at, f, vel, pan, [[1, 1, 1.8], [2.76, 0.35, 0.5], [5.4, 0.18, 0.2]], [0.5, 0.6, 0.25]);
  else {
    // A music box: tines a little out of tune with each other (an old box).
    const old = 1 + (Math.random() - 0.5) * 0.008;
    partials(ctx, bus, at, f * old, vel, pan, [[1, 1, 1.3], [3.0, 0.2, 0.35], [6.2, 0.08, 0.12]], [0.45, 0.75, 0.3]);
  }
}

export type MalletKind = "marimba" | "vibraphone";
export function mallet(ctx: Ctx, bus: Bus, at: number, midi: number, vel: number, kind: MalletKind, pan: number) {
  const rec = recorded(ctx, `${kind}.hit`, at, midi, vel, null);
  if (rec) { if (kind === "marimba") send(ctx, bus, rec, pan, 0.65, 0.35, 0.1); else send(ctx, bus, rec, pan, 0.5, 0.6, 0.15); return; }
  const f = hz(midi);
  if (kind === "marimba") { partials(ctx, bus, at, f, vel, pan, [[1, 1, 0.5 + 60 / f], [4, 0.28, 0.1], [10, 0.06, 0.03]], [0.65, 0.35, 0.1]); return; }
  // Vibraphone: two partials with the motor's tremolo.
  const amp = ctx.createGain(); amp.gain.value = 1;
  const trem = ctx.createOscillator(); trem.frequency.value = 5.2;
  const tg = ctx.createGain(); tg.gain.value = 0.3;
  trem.connect(tg); tg.connect(amp.gain); trem.start(at); trem.stop(at + 3.6);
  const inner = ctx.createGain(); inner.gain.value = vel; inner.connect(amp);
  for (const [r, a, d] of [[1, 1, 3.4], [4, 0.14, 0.4]] as const) {
    const o = ctx.createOscillator(); o.frequency.value = f * r;
    const g = ctx.createGain(); g.gain.setValueAtTime(0, at); g.gain.linearRampToValueAtTime(a, at + 0.004); g.gain.exponentialRampToValueAtTime(0.0001, at + d);
    o.connect(g); g.connect(inner); o.start(at); o.stop(at + d + 0.05);
  }
  send(ctx, bus, amp, pan, 0.5, 0.6, 0.15);
}

/** A felt piano: slightly stretched partials, the high ones dying first, a soft hammer, all through felt (low-pass). */
export function piano(ctx: Ctx, bus: Bus, at: number, midi: number, vel: number, pan: number) {
  const rec = recorded(ctx, "piano.soft", at, midi, vel, null);
  if (rec) {
    // The upright's soft layer through a little felt: darker the softer it's played.
    const felt = ctx.createBiquadFilter(); felt.type = "lowpass"; felt.frequency.value = 1800 + vel * 9000; felt.Q.value = 0.3;
    rec.connect(felt);
    send(ctx, bus, felt, pan, 0.55, 0.55, 0.12);
    return;
  }
  const f = hz(midi);
  const B = 0.00035, life = clamp(5.5 - (midi - 48) * 0.06, 1.6, 6);
  const amp = ctx.createGain(); amp.gain.value = vel;
  const felt = ctx.createBiquadFilter(); felt.type = "lowpass"; felt.frequency.value = 900 + vel * 5000; felt.Q.value = 0.3;
  amp.connect(felt);
  for (let k = 1; k <= 7; k++) {
    const fk = f * k * Math.sqrt(1 + B * k * k);
    if (fk > 12000) break;
    const o = ctx.createOscillator(); o.frequency.value = fk; o.detune.value = (Math.random() - 0.5) * 3;
    const g = ctx.createGain(); const d = life / Math.pow(k, 0.75);
    g.gain.setValueAtTime(0, at); g.gain.linearRampToValueAtTime(1 / Math.pow(k, 1.4), at + 0.004);
    g.gain.exponentialRampToValueAtTime(0.35 / Math.pow(k, 1.4), at + 0.25); g.gain.exponentialRampToValueAtTime(0.0001, at + d);
    o.connect(g); g.connect(amp); o.start(at); o.stop(at + d + 0.05);
  }
  const h = ctx.createBufferSource(); h.buffer = noise(ctx);
  const hb = ctx.createBiquadFilter(); hb.type = "bandpass"; hb.frequency.value = Math.min(4000, f * 4); hb.Q.value = 0.7;
  const hg = ctx.createGain(); hg.gain.setValueAtTime(0.08, at); hg.gain.exponentialRampToValueAtTime(0.0001, at + 0.03);
  h.connect(hb); hb.connect(hg); hg.connect(amp); h.start(at, Math.random()); h.stop(at + 0.05);
  send(ctx, bus, felt, pan, 0.55, 0.55, 0.12);
}

/** The soft FM electric piano (the first voice the score had). */
export function ep(ctx: Ctx, bus: Bus, at: number, midi: number, vel: number, pan: number) {
  const f = hz(midi);
  const car = ctx.createOscillator(); car.frequency.value = f;
  const mod = ctx.createOscillator(); mod.frequency.value = f;
  const idx = ctx.createGain();
  idx.gain.setValueAtTime(f * (1.1 + vel * 2.2), at); idx.gain.exponentialRampToValueAtTime(f * 0.12, at + 1.4);
  mod.connect(idx); idx.connect(car.frequency);
  const tine = ctx.createOscillator(); tine.frequency.value = f * 14;
  const tg = ctx.createGain(); tg.gain.setValueAtTime(f * 0.5 * vel, at); tg.gain.exponentialRampToValueAtTime(0.01, at + 0.06);
  tine.connect(tg); tg.connect(car.frequency);
  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0, at); amp.gain.linearRampToValueAtTime(vel, at + 0.006);
  amp.gain.exponentialRampToValueAtTime(vel * 0.35, at + 0.7); amp.gain.exponentialRampToValueAtTime(0.0001, at + 3.6);
  const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 3800; lp.Q.value = 0.3;
  car.connect(amp); amp.connect(lp);
  send(ctx, bus, lp, pan, 0.55, 0.5, 0.18);
  for (const o of [car, mod, tine]) { o.start(at); o.stop(at + 3.7); }
}

// ---- sustained beds ---------------------------------------------------------------------------------------------------

/** A pad note: two detuned soft-harmonic oscillators through a breathing low-pass, swelling in over seconds. */
export function pad(ctx: Ctx, bus: Bus, at: number, midi: number, vel: number, pan: number, cutoff: number, swell: number): Held {
  const f = hz(midi);
  const a = ctx.createOscillator(); a.setPeriodicWave(wave(ctx, "soft")); a.frequency.value = f; a.detune.value = -7;
  const b = ctx.createOscillator(); b.setPeriodicWave(wave(ctx, "soft")); b.frequency.value = f; b.detune.value = 7;
  const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = cutoff; lp.Q.value = 0.4;
  const depth = ctx.createGain(); depth.gain.value = cutoff * 0.35;
  bus.lfo.connect(depth); depth.connect(lp.frequency);
  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0, at); amp.gain.linearRampToValueAtTime(vel, at + swell);
  a.connect(lp); b.connect(lp); lp.connect(amp);
  send(ctx, bus, amp, pan, 0.45, 0.75);
  a.start(at); b.start(at);
  return {
    stop(t: number) {
      const r = Math.max(t, at + 0.05);
      amp.gain.cancelScheduledValues(r); amp.gain.setTargetAtTime(0, r, swell / 3);
      a.stop(r + swell * 2); b.stop(r + swell * 2);
      a.onended = () => depth.disconnect();
    },
  };
}

/** A sung "aah": three slightly different voices through the vowel's formants, swelling slowly. */
export function choir(ctx: Ctx, bus: Bus, at: number, midi: number, vel: number, pan: number, swell: number): Held {
  const f = hz(midi);
  const sum = ctx.createGain(); sum.gain.value = 1 / 3;
  const oscs: OscillatorNode[] = [];
  for (const d of [-9, 0, 8]) {
    const o = ctx.createOscillator(); o.type = "sawtooth"; o.frequency.value = f; o.detune.value = d;
    vibrato(ctx, o.frequency, at, f, 4.6 + d * 0.03, 0.003, 0.5, at + 60);
    o.connect(sum); oscs.push(o);
  }
  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0, at); amp.gain.linearRampToValueAtTime(vel, at + swell);
  // "ah": formants near 730, 1090 and 2440 Hz.
  for (const [fr, q, g] of [[730, 7, 1], [1090, 9, 0.55], [2440, 11, 0.25]] as const) {
    const bp = ctx.createBiquadFilter(); bp.type = "bandpass"; bp.frequency.value = fr; bp.Q.value = q;
    const gg = ctx.createGain(); gg.gain.value = g * 3.2;
    sum.connect(bp); bp.connect(gg); gg.connect(amp);
  }
  send(ctx, bus, amp, pan, 0.35, 0.9);
  oscs.forEach((o) => o.start(at));
  return {
    stop(t: number) {
      const r = Math.max(t, at + 0.05);
      amp.gain.cancelScheduledValues(r); amp.gain.setTargetAtTime(0, r, swell / 3);
      oscs.forEach((o) => o.stop(r + swell * 2));
    },
  };
}

/** The bass under the pad: a round sine with a little body, held for the chord. */
export function sub(ctx: Ctx, bus: Bus, at: number, midi: number, vel: number, len: number | null): Held {
  const o = ctx.createOscillator(); o.frequency.value = hz(midi);
  const body = ctx.createOscillator(); body.setPeriodicWave(wave(ctx, "soft")); body.frequency.value = hz(midi);
  const bg = ctx.createGain(); bg.gain.value = 0.18;
  const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 260;
  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0, at); amp.gain.linearRampToValueAtTime(vel, at + (len ? 0.02 : 0.9));
  o.connect(lp); body.connect(bg); bg.connect(lp); lp.connect(amp);
  send(ctx, bus, amp, 0, 0.9, 0.15);
  o.start(at); body.start(at);
  const stop = (t: number) => {
    const r = Math.max(t, at + 0.05);
    amp.gain.cancelScheduledValues(r); amp.gain.setTargetAtTime(0, r, len ? 0.08 : 0.8);
    o.stop(r + (len ? 0.5 : 4)); body.stop(r + (len ? 0.5 : 4));
  };
  if (len) stop(at + len);
  return { stop };
}

/** A sudden event: a low thud, a timpani-like swell, and a tubular bell far off. */
export function stinger(ctx: Ctx, bus: Bus, at: number, root: number) {
  const o = ctx.createOscillator(); o.frequency.setValueAtTime(hz(root - 12) * 1.5, at); o.frequency.exponentialRampToValueAtTime(hz(root - 12), at + 0.4);
  const g = ctx.createGain(); g.gain.setValueAtTime(0, at); g.gain.linearRampToValueAtTime(0.5, at + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, at + 2.5);
  o.connect(g); send(ctx, bus, g, 0, 0.7, 0.6);
  o.start(at); o.stop(at + 2.6);
  bell(ctx, bus, at + 0.05, root + 12, 0.08, "tubular", -0.3);
  bell(ctx, bus, at + 0.14, root + 31, 0.05, "glockenspiel", 0.4);
}
